import { BFF_BASE_URL, getTenantId } from './constants';

export type ChatErrorKind = 'session_expired' | 'service_unavailable' | 'rate_limited' | 'message_too_long' | 'network' | 'unknown';

export class ChatError extends Error {
  constructor(
    public readonly kind: ChatErrorKind,
    message: string,
    options?: ErrorOptions,
  ) {
    super(message, options);
    this.name = 'ChatError';
  }
}

const ERROR_MESSAGES: Record<ChatErrorKind, string> = {
  session_expired: 'Your session has expired. Please sign in again.',
  service_unavailable: 'The AI service is temporarily unavailable. Please try again.',
  rate_limited: "Porter couldn't complete this request at the moment. Please wait and try again later.",
  message_too_long: 'Your question is too long. Please shorten it and try again.',
  network: 'Unable to connect. Check your network.',
  unknown: 'Something went wrong. Please try again.',
};

function classifyError(status: number): ChatErrorKind {
  if (status === 401) return 'session_expired';
  if (status === 429) return 'rate_limited';
  if (status === 503) return 'service_unavailable';
  return 'unknown';
}

/** UI control the client should render, derived server-side from reason_code. */
export type Control = 'CHANGE_QUESTION' | 'REFRESH' | 'NONE';

/**
 * Leak-safe disambiguation discriminator: `'disambiguate'` (2–5 matches,
 * candidates present), `'too_many'` (>5 — identities/count withheld), or
 * `null` for a normal answered turn.
 */
export type DisambiguationKind = 'disambiguate' | 'too_many' | null;

export interface ChatResponse {
  response: string;
  /** Non-answer reason code (e.g. DATA_ABSENT, SYSTEM_ERROR); null on success. */
  reasonCode: string | null;
  /** Control to render: CHANGE_QUESTION, REFRESH, or NONE. */
  control: Control;
  disambiguationCandidates: DisambiguationCandidate[];
  /**
   * Leak-safe discriminator for a disambiguation turn: `'disambiguate'`
   * (2–5 matches, candidates present) or `'too_many'` (>5 matches — identities
   * and count withheld). `null` for a normal answered turn. Drives which
   * affordance the UI renders (pick-list vs. an "Add more context" prompt)
   * without inferring the outcome from the response text.
   */
  disambiguationKind: DisambiguationKind;
  /**
   * The single client this turn resolved to, so the UI can pin the
   * "Searching for {client}" context tag directly from a chat turn (mirrors
   * the GET /history entity fields). `null` when the turn did not resolve to
   * exactly one client (zero / multiple / client-scope conflict / any
   * disambiguation turn / system error). Always populated by
   * {@link parseChatResponse}; optional so hand-built responses (tests) need
   * not restate it.
   */
  entityId?: string | null;
  /** Point-in-time client snapshot for {@link entityId}; null when unresolved. */
  entityDetails?: EntityDetails | null;
}

/**
 * A single client identity offered in a disambiguation pick-list (2–5 matches).
 * camelCase, 1:1 with the backend `DisambiguationCandidateSchema` and with the
 * `ClientContext` container so a selection maps straight onto
 * `useClientContext().set()` (`clientId` → `id`). Empty for all other outcomes
 * (including too-many, which withholds identities).
 */
export interface DisambiguationCandidate {
  clientId: string;
  firstName?: string;
  lastName?: string;
  preferredName?: string;
  dateOfBirth?: string;
  clientAdviserName?: string;
  preferredSuburb?: string;
  preferredState?: string;
}

/**
 * Point-in-time client display snapshot attached to a history item.
 * Mirrors the backend `entity_details` map (see GET /history), used to
 * render the history entry label and the "Searching for" context tag
 * without any live client lookup.
 */
export interface EntityDetails {
  firstName?: string;
  lastName?: string;
  preferredName?: string;
  dateOfBirth?: string;
  clientAdviserName?: string;
  preferredSuburb?: string;
  preferredState?: string;
}

/**
 * A single query-history item as consumed by the UI (camelCase).
 * Maps 1:1 from the backend `GET /history` response item.
 */
export interface HistoryItem {
  queryId: string;
  queryText: string;
  responseText: string;
  status: string;
  createdAt: string;
  entityId: string | null;
  entityDetails: EntityDetails | null;
  toolsUsed: string[];
  sourceLinks: string[];
}

// ── Onboarding / Preferences types ──────────────────────────────────────────

/**
 * A single instructional point in the onboarding card.
 */
export interface OnboardingPoint {
  title: string;
  description: string;
  /** Material Symbol icon name (e.g. 'search', 'home') */
  icon?: string;
}

/**
 * Configurable onboarding content delivered with preferences.
 */
export interface OnboardingContent {
  points: OnboardingPoint[];
  contextualTip: OnboardingPoint;
  disclaimer: string;
}

/**
 * Response from GET /preferences and PUT /preferences.
 */
export interface PreferenceResponse {
  onboardingDismissed: boolean;
  content: OnboardingContent;
}

/** Default fallback content when preferences fetch fails. */
const DEFAULT_ONBOARDING_CONTENT: OnboardingContent = {
  points: [
    {
      title: 'Fetch your data in seconds',
      description: 'Porter answers based on data strictly from your active Xplan record.',
      icon: 'search',
    },
    {
      title: 'Your file, your decisions',
      description: 'Porter reads and reports only; no advice or recommendations.',
      icon: 'eyeglasses_3',
    },
    {
      title: 'No guesswork',
      description: 'If something is missing or unclear, Porter says so rather than estimating.',
      icon: 'science_off',
    },
  ],
  contextualTip: {
    title: 'One client at a time.',
    description: 'We are working on improving Porter.\nMore capabilities are coming soon.',
  },
  disclaimer: 'AI-generated from Xplan data|Verify before use|No financial advice provided',
};

/**
 * Submits a chat message and returns the response.
 *
 * When `currentClientId` is provided (a client context is active), it is
 * sent as `current_client_id` in the request body so the agent scopes the
 * query to that client. When omitted, the field is left out entirely — a
 * context-free query. Permission re-validation for the client id happens at
 * the tool / backend-LLM level; the UI only forwards the id.
 *
 * When `sessionId` is provided, it is sent as `session_id` in the request
 * body so the backend appends the completed turn to the active session.
 */
export async function submitChat(
  message: string,
  signal?: AbortSignal,
  currentClientId?: string,
  queryId?: string,
  sessionId?: string,
): Promise<ChatResponse> {
  let response: Response;

  const body: { message: string; current_client_id?: string; query_id?: string; session_id?: string } = { message };
  if (currentClientId) {
    body.current_client_id = currentClientId;
  }
  if (queryId) {
    body.query_id = queryId;
  }
  if (sessionId) {
    body.session_id = sessionId;
  }

  try {
    response = await fetch(`${BFF_BASE_URL}/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'tenant-id': await getTenantId(),
      },
      body: JSON.stringify(body),
      signal,
    });
  } catch (err) {
    if (signal?.aborted) throw new ChatError('network', 'Request cancelled.');
    throw new ChatError('network', ERROR_MESSAGES.network);
  }

  if (!response.ok) {
    await throwChatError(response);
  }

  const data = await response.json();
  return parseChatResponse(data);
}

/**
 * Maps a non-OK `/chat` response to the appropriate {@link ChatError} and
 * throws it. Extracted from {@link submitChat} to keep that function's
 * branching low. Never returns — always throws.
 */
async function throwChatError(response: Response): Promise<never> {
  if (response.status === 400) {
    const errorCode = await response
      .json()
      .then((b) => (b as { error_code?: string })?.error_code)
      .catch(() => undefined);
    if (errorCode === 'MESSAGE_TOO_LONG') {
      throw new ChatError('message_too_long', ERROR_MESSAGES.message_too_long);
    }
  }
  const kind = classifyError(response.status);
  throw new ChatError(kind, ERROR_MESSAGES[kind]);
}

/**
 * Request cancellation of an in-flight query.
 * Fires-and-forgets — the UI has already aborted the fetch locally; this
 * signals the backend to halt the running agent task as well.
 */
export async function cancelChat(queryId: string): Promise<void> {
  try {
    await fetch(`${BFF_BASE_URL}/chat/${encodeURIComponent(queryId)}/cancel`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'tenant-id': await getTenantId(),
      },
    });
  } catch {
    // Best-effort — the UI has already stopped consuming the response.
    // A failed cancel request is not surfaced to the user.
  }
}

/**
 * Normalizes the raw `/chat` JSON into a typed {@link ChatResponse}, applying
 * the leak-safe defaults. Extracted from {@link submitChat} to keep that
 * function's branching low.
 */
function parseChatResponse(data: {
  response?: string;
  reason_code?: string | null;
  control?: string;
  disambiguationCandidates?: unknown;
  disambiguationKind?: string;
  entityId?: string | null;
  entityDetails?: RawHistoryItem['entity_details'];
}): ChatResponse {
  const control: Control =
    data.control === 'CHANGE_QUESTION' || data.control === 'REFRESH'
      ? data.control
      : 'NONE';
  const disambiguationKind: DisambiguationKind =
    data.disambiguationKind === 'disambiguate' ||
    data.disambiguationKind === 'too_many'
      ? data.disambiguationKind
      : null;
  return {
    response: data.response ?? '',
    reasonCode: data.reason_code ?? null,
    control,
    // Backend serializes camelCase (disambiguationCandidates); default to [].
    disambiguationCandidates: Array.isArray(data.disambiguationCandidates)
      ? (data.disambiguationCandidates as DisambiguationCandidate[])
      : [],
    // Leak-safe discriminator; only 'disambiguate' / 'too_many' are meaningful.
    disambiguationKind,
    // Resolved single-client entity for the context tag (mirrors /history).
    // Reuses the same defensive mapping so a chat-sourced tag is identical to
    // a history-sourced one. null when the turn didn't resolve one client.
    entityId: data.entityId ?? null,
    entityDetails: mapEntityDetails(data.entityDetails),
  };
}

/** Raw history item shape as returned by the backend (snake_case). */
interface RawHistoryItem {
  query_id: string;
  query_text: string;
  response_text: string;
  status: string;
  created_at: string;
  entity_id?: string | null;
  // The backend serializes entity_details with camelCase aliases
  // (firstName, lastName, dateOfBirth, clientAdviserName). snake_case keys are
  // accepted too as a defensive fallback in case the schema aliases change.
  entity_details?: {
    firstName?: string | null;
    lastName?: string | null;
    preferredName?: string | null;
    dateOfBirth?: string | null;
    clientAdviserName?: string | null;
    preferredSuburb?: string | null;
    preferredState?: string | null;
    first_name?: string | null;
    last_name?: string | null;
    preferred_name?: string | null;
    date_of_birth?: string | null;
    client_adviser_name?: string | null;
    preferred_suburb?: string | null;
    preferred_state?: string | null;
  } | null;
  tools_used?: string[];
  source_links?: string[];
}

/** First non-nullish of a camelCase / snake_case pair, normalised to undefined. */
function pickField(camel?: string | null, snake?: string | null): string | undefined {
  return camel ?? snake ?? undefined;
}

function mapEntityDetails(
  raw: RawHistoryItem['entity_details'],
): EntityDetails | null {
  if (!raw) return null;
  const details: EntityDetails = {
    firstName: pickField(raw.firstName, raw.first_name),
    lastName: pickField(raw.lastName, raw.last_name),
    preferredName: pickField(raw.preferredName, raw.preferred_name),
    dateOfBirth: pickField(raw.dateOfBirth, raw.date_of_birth),
    clientAdviserName: pickField(raw.clientAdviserName, raw.client_adviser_name),
    preferredSuburb: pickField(raw.preferredSuburb, raw.preferred_suburb),
    preferredState: pickField(raw.preferredState, raw.preferred_state),
  };
  // If every field is empty, treat as no details (renders as generic label).
  const hasAny = Object.values(details).some((v) => v != null && v !== '');
  return hasAny ? details : null;
}

function mapHistoryItem(raw: RawHistoryItem): HistoryItem {
  return {
    queryId: raw.query_id,
    queryText: raw.query_text,
    responseText: raw.response_text,
    status: raw.status,
    createdAt: raw.created_at,
    entityId: raw.entity_id ?? null,
    entityDetails: mapEntityDetails(raw.entity_details),
    toolsUsed: raw.tools_used ?? [],
    sourceLinks: raw.source_links ?? [],
  };
}

/**
 * Fetches the user's recent query history from `GET /history`.
 *
 * The backend already returns the display-capped (max 10), newest-first,
 * non-client-excluded list — the UI does not paginate or re-sort the cap.
 * Returns items mapped to the camelCase {@link HistoryItem} shape.
 */
export async function fetchHistory(signal?: AbortSignal): Promise<HistoryItem[]> {
  let response: Response;

  try {
    response = await fetch(`${BFF_BASE_URL}/history`, {
      method: 'GET',
      headers: {
        'tenant-id': await getTenantId(),
      },
      signal,
    });
  } catch (err) {
    if (signal?.aborted) throw new ChatError('network', 'Request cancelled.');
    throw new ChatError('network', ERROR_MESSAGES.network);
  }

  if (!response.ok) {
    const kind = classifyError(response.status);
    throw new ChatError(kind, ERROR_MESSAGES[kind]);
  }

  const data = await response.json();
  const items: RawHistoryItem[] = data.items ?? [];
  return items.map(mapHistoryItem);
}

/**
 * Fetches a single client's display snapshot from `GET /client/{id}`.
 *
 * Used to build the "Searching for …" context tag when a client is
 * auto-detected from the XPLAN URL at Porter launch. Best-effort by contract:
 * returns `null` on any non-2xx (including 404 "not a client"), network error,
 * or malformed body — the caller treats `null` as "no client context" and opens
 * without a tag, never blocking or erroring (Scenario 4).
 */
export async function fetchClientDetails(
  id: string,
  signal?: AbortSignal,
): Promise<EntityDetails | null> {
  let response: Response;

  try {
    response = await fetch(`${BFF_BASE_URL}/client/${encodeURIComponent(id)}`, {
      method: 'GET',
      headers: {
        'tenant-id': await getTenantId(),
      },
      signal,
    });
  } catch {
    // Network error or abort — best-effort, never throw to the caller.
    return null;
  }

  if (!response.ok) return null;

  try {
    const data = await response.json();
    // Reuse the same defensive camelCase/snake_case mapping as history.
    return mapEntityDetails(data);
  } catch {
    return null;
  }
}

/**
 * A single completed turn stored in a chat session, as returned by
 * ``GET /session/{sessionId}``.
 */
export interface SessionTurn {
  query: string;
  response: string;
  status: string;
  control: Control;
  reasonCode: string | null;
  disambiguationKind: DisambiguationKind;
  candidates: DisambiguationCandidate[];
}

/**
 * A chat session as returned by ``GET /session/{sessionId}``.
 * ``activeClientId`` is null when the session has no pinned client context.
 * ``activeClientDetails`` is the point-in-time display snapshot of that pinned
 * client (name/DOB/adviser), or null — used to restore the full context tag on
 * reload rather than a bare id.
 */
export interface SessionData {
  sessionId: string;
  turns: SessionTurn[];
  activeClientId: string | null;
  activeClientDetails: EntityDetails | null;
}

/** Raw session turn shape as returned by the backend (snake_case optional fields). */
interface RawSessionTurn {
  query: string;
  response: string;
  status: string;
  control: string;
  reasonCode?: string | null;
  reason_code?: string | null;
  disambiguationKind?: string | null;
  disambiguation_kind?: string | null;
  candidates?: unknown[];
}

function parseControl(raw: string | undefined): Control {
  return raw === 'CHANGE_QUESTION' || raw === 'REFRESH' ? raw : 'NONE';
}

function parseDisambiguationKind(raw: string | null | undefined): DisambiguationKind {
  return raw === 'disambiguate' || raw === 'too_many' ? raw : null;
}

function mapSessionTurn(raw: RawSessionTurn): SessionTurn {
  const rawKind = raw.disambiguationKind ?? raw.disambiguation_kind;
  return {
    query: raw.query,
    response: raw.response,
    status: raw.status,
    control: parseControl(raw.control),
    reasonCode: raw.reasonCode ?? raw.reason_code ?? null,
    disambiguationKind: parseDisambiguationKind(rawKind),
    candidates: Array.isArray(raw.candidates)
      ? (raw.candidates as DisambiguationCandidate[])
      : [],
  };
}

/**
 * Fetches the active chat session from ``GET /session/{sessionId}``.
 *
 * Returns ``null`` when the session does not exist (404). Throws a
 * ``ChatError`` for all other error responses.
 */
export async function fetchSession(
  sessionId: string,
  signal?: AbortSignal,
): Promise<SessionData | null> {
  let response: Response;

  try {
    response = await fetch(`${BFF_BASE_URL}/session/${sessionId}`, {
      method: 'GET',
      headers: {
        'tenant-id': await getTenantId(),
      },
      signal,
    });
  } catch (error_) {
    if (signal?.aborted) throw new ChatError('network', 'Request cancelled.');
    if (error_ instanceof ChatError) throw error_;
    throw new ChatError('network', ERROR_MESSAGES.network);
  }

  // 404 = session not found; return null so the caller starts fresh.
  if (response.status === 404) return null;

  if (!response.ok) {
    const kind = classifyError(response.status);
    throw new ChatError(kind, ERROR_MESSAGES[kind]);
  }

  const data = await response.json();
  const rawTurns: RawSessionTurn[] = data.turns ?? [];
  return {
    sessionId: data.sessionId ?? sessionId,
    turns: rawTurns.map(mapSessionTurn),
    activeClientId: data.activeClientId ?? null,
    // Reuse the shared camelCase/snake_case entity-details mapping (same shape
    // as /history and /chat). Returns null when absent or all-empty.
    activeClientDetails: mapEntityDetails(
      data.activeClientDetails ?? data.active_client_details ?? null,
    ),
  };
}

/**
 * Deletes the active chat session via ``DELETE /session/{sessionId}``.
 *
 * Best-effort: errors are swallowed so a storage failure never blocks the
 * "New chat" UX flow. The session is gone from DynamoDB regardless of whether
 * this call succeeds (TTL will clean it up).
 */
export async function deleteSession(sessionId: string): Promise<void> {
  try {
    await fetch(`${BFF_BASE_URL}/session/${sessionId}`, {
      method: 'DELETE',
      headers: {
        'tenant-id': await getTenantId(),
      },
    });
  } catch (error_) {
    // Best-effort — never block the UI flow for a delete error.
    // Log at debug level so the failure is visible without alarming the user.
    console.debug('deleteSession failed:', error_);
  }
}


// ── Preferences API ─────────────────────────────────────────────────────────

/**
 * Fetches the user's onboarding preferences from `GET /preferences`.
 *
 * Returns the preference state and configurable onboarding content.
 *
 * `GET /preferences` returns HTTP 200 even for a first-time user with no
 * stored record (`onboardingDismissed: false`), so a non-ok response or a
 * network failure is a genuine backend/network failure — not the "no
 * preferences yet" case. Because this is Porter's open-time call, such a
 * failure is surfaced as a {@link ChatError} (classified by status) so the
 * caller can render the shell-level page-level error on open. A missing
 * `content` field on an otherwise-ok response still falls back to
 * {@link DEFAULT_ONBOARDING_CONTENT}.
 */
export async function fetchPreferences(): Promise<PreferenceResponse> {
  let response: Response;

  try {
    response = await fetch(`${BFF_BASE_URL}/preferences`, {
      method: 'GET',
      headers: {
        'tenant-id': await getTenantId(),
      },
    });
  } catch (err) {
    // Could not reach the server — a network failure on open.
    throw new ChatError('network', ERROR_MESSAGES.network, { cause: err });
  }

  if (!response.ok) {
    const kind = classifyError(response.status);
    throw new ChatError(kind, ERROR_MESSAGES[kind]);
  }

  const data = await response.json();
  return {
    onboardingDismissed: data.onboardingDismissed ?? false,
    content: data.content ?? DEFAULT_ONBOARDING_CONTENT,
  };
}

/**
 * Updates the user's onboarding preferences via `PUT /preferences`.
 *
 * This is an optimistic update — the UI immediately hides the onboarding card
 * and doesn't block on the response. Errors are logged but not surfaced to
 * the user (minor consequence if the preference isn't persisted).
 */
export async function updatePreferences(dismissed: boolean): Promise<void> {
  try {
    const response = await fetch(`${BFF_BASE_URL}/preferences`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'tenant-id': await getTenantId(),
      },
      body: JSON.stringify({ onboardingDismissed: dismissed }),
    });

    if (!response.ok) {
      console.warn('Failed to update preferences:', response.status);
    }
  } catch (err) {
    // Network error — log and continue (optimistic update)
    console.warn('Network error updating preferences', err);
  }
}
